import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import Footer from '../../../../../components/Footer/Footer';
import InnerNav from '../../../../../components/InnerNav/InnerNav';
import { useFormData } from '../../ContextData/ContextData';

import './Pras.scss';

const Pras = () => {
  const { userData } = useFormData();
  const [prasData, setPrasData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ascSign, setAscSign] = useState(1);

  // Coordinates of houses inside diamond
  const houseSlots = {
    1: [100, 350],
    2: [310, 360],
    3: [360, 300],
    4: [370, 90],
    5: [300, 40],
    6: [90, 30],
    7: [40, 100],
    8: [30, 310],
    9: [150, 200],
    10: [200, 270],
    11: [270, 200],
    12: [200, 130],
  };

  // Rotate houses based on ascendant sign
  const getRotatedSign = (houseIdx) => {
    if (!ascSign) return houseIdx;
    return ((ascSign - 1 + (houseIdx - 1)) % 12) + 1;
  };

  // Color mapping by auspiciousness
  const getDotColor = (signNum, chartMeta) => {
    const signMap = {
      1: 'Aries',
      2: 'Taurus',
      3: 'Gemini',
      4: 'Cancer',
      5: 'Leo',
      6: 'Virgo',
      7: 'Libra',
      8: 'Scorpio',
      9: 'Sagittarius',
      10: 'Capricorn',
      11: 'Aquarius',
      12: 'Pisces',
    };
    const signName = signMap[signNum];

    if (chartMeta?.auspicious?.includes(signName)) return 'orange';
    if (chartMeta?.inauspicious?.includes(signName)) return 'dodgerblue';
    if (chartMeta?.mixed?.includes(signName)) return 'seagreen';
    return 'grey';
  };

  // Fetch Prasthara data
  useEffect(() => {
    const fetchPrasthara = async () => {
      try {
        const { data } = await axios.get(
          'https://core-c343.onrender.com/api/prasthara',
          {
            params: {
              year: userData.year,
              month: userData.month,
              day: userData.day,
              hour: userData.hour,
              minute: userData.minute,
            },
          }
        );
        setPrasData(data.prastharashtakavarga || []);
        setAscSign(1);
      } catch (err) {
        setError(err.message || 'Error fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchPrasthara();
  }, [userData]);

  // Chart Canvas component
  const ChartCanvas = ({ data }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
      if (!canvasRef.current || !data) return;

      const bindus = data.grid.find((g) => g.planet === 'Total')?.bindus || [];
      const chartMeta = data.chartMeta || {};

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Outer diamond
      ctx.beginPath();
      ctx.moveTo(200, 0);
      ctx.lineTo(400, 200);
      ctx.lineTo(200, 400);
      ctx.lineTo(0, 200);
      ctx.closePath();
      ctx.strokeStyle = '#FF8C42';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Cross diagonals
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(400, 400);
      ctx.moveTo(400, 0);
      ctx.lineTo(0, 400);
      ctx.stroke();

      // Draw bindus
      Object.keys(houseSlots).forEach((houseIdx) => {
        const realSign = getRotatedSign(parseInt(houseIdx));
        const binduValue = bindus[realSign - 1] || 0;
        const [x, y] = houseSlots[houseIdx];

        // Circle
        ctx.beginPath();
        ctx.arc(x, y, 20, 0, 2 * Math.PI);
        ctx.fillStyle = getDotColor(realSign, chartMeta);
        ctx.fill();

        // Number inside circle
        ctx.fillStyle = 'white';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(binduValue, x, y);

        // House number
        ctx.fillStyle = 'black';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        const rotatedHouseNum = getRotatedSign(parseInt(houseIdx));
        ctx.fillText(rotatedHouseNum, x, y + 25);
      });
    }, [data]);

    return (
      <div className="chart"style={{ marginBottom: '30px', textAlign: 'right' }}>
        <div className="left-chart">
          <h2>Indicator</h2>
          <p style={{ color: 'dodgerblue' }}>Auspicious Sign</p>
          <p style={{ color: 'orange' }}>Inauspicious Sign</p>
          <p style={{ color: 'seagreen' }}>Mixed Sign</p>
          <p style={{ color: 'grey' }}>Neutral Sign</p>
          <h2>Significance</h2>
          <p style={{ color: 'orange' }}>Father</p>
          <p style={{ color: 'green' }}>Government</p>
        </div>
        <canvas
          ref={canvasRef}
          width={400}
          height={400}
          style={{ border: '1px solid black' }}
        ></canvas>
      </div>
    );
  };

  if (loading) return <p className="loading-text">Loading...</p>;
  if (error) return <p className="error-text">{error}</p>;

  return (
    <div className="prasthara-container">
      <InnerNav />
      {prasData.map((pras, idx) => {
        const headers = pras.signs || [];
        return (
          <div key={idx} className="prasthara-section">
            <h2>Prastharashtakvarga – {pras.referencePlanet}</h2>
            <table className="prasthara-table">
              <thead>
                <tr>
                  <th>Planet</th>
                  {headers.map((head, i) => (
                    <th key={i}>{head}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pras.grid.map((row, i) => (
                  <tr key={i}>
                    <td className="planet-name">{row.planet}</td>
                    {row.bindus.map((bindu, j) => (
                      <td key={j}>{bindu}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Chart comes right after the table */}
            <ChartCanvas data={pras} />
          </div>
        );
      })}
      <Footer />
    </div>
  );
};

export default Pras;
